/**
 * #panel-collapse layout math: floors, defaults, and fit-to-frame.
 * Production port (Wave 3) reads the floors from here or from shell-chrome.
 */
import { Bot, FolderTree, Inbox, Workflow } from 'lucide-react'
import { SHELL_ICON_RAIL_PX } from '@/lib/shell-chrome'
import type { Workspace } from '@/providers/workspace-provider'
import type { GalleryMenuContext } from './maater-ui-gallery-entity-menu'

export type PanelCollapseMode = 'browse' | 'inbox' | 'flows' | 'chats'

export const RAIL = SHELL_ICON_RAIL_PX
/** 48px rail + about 200px of labels. Below this the left column is the rail. */
export const LEFT_FLOOR = 248
/** Below this the agent column is the 48px strip. */
export const AGENT_FLOOR = 320
/** Inbox preview. Below this it closes. There is no preview rail. */
export const PREVIEW_FLOOR = 280
export const CANVAS_MIN = 160
export const LEFT_DEFAULT = 280
export const AGENT_DEFAULT = 360
export const PREVIEW_DEFAULT = 300
export const FRAME_MIN = 360
export const FRAME_MAX = 1400
export const FRAME_DEFAULT = 1200

const workspace = (id: string, name: string, color: string): Workspace => ({
  id,
  name,
  slug: id,
  type: 'work',
  icon: null,
  color,
  contentRoot: null,
  showInSidebar: true,
})

/** Rail scope fixture: five pips plus +2, the same glyph as the live icon bar. */
export const SCOPE_WORKSPACES: Workspace[] = [
  workspace('hop', 'Hop Studios', '#3b82f6'),
  workspace('maater', 'Maater', '#a855f7'),
  workspace('personal', 'Personal', '#22c55e'),
  workspace('ggia', 'GGIA', '#f59e0b'),
  workspace('ggsc', 'GGSC', '#ef4444'),
  workspace('community', 'Community', '#14b8a6'),
  workspace('archive', 'Archive', '#64748b'),
]
export const SCOPE_VISIBLE_IDS = SCOPE_WORKSPACES.slice(0, 5).map((w) => w.id)
export const SCOPE_OVERFLOW = SCOPE_WORKSPACES.length - SCOPE_VISIBLE_IDS.length

const DOC_PATH = 'hop-studios/internal/late-todo-shadow.md'
const FOLDER_PATH = 'hop-studios/documents'

/** Production menu contexts for each trigger in the scene (ADR-0020). */
export const MENU_CONTEXTS = {
  inboxTab: { type: 'tab', value: 'Inbox', tabId: 'inbox', tabType: 'inbox', shareUrl: '/inbox' },
  documentsTab: {
    type: 'tab',
    value: 'Documents',
    tabId: 'explorer-hop-documents',
    tabType: 'explorer',
    path: FOLDER_PATH,
    hasFilePath: false,
    shareUrl: `/${FOLDER_PATH}`,
    workspaceGroupId: 'hop',
  },
  docTab: {
    type: 'tab',
    value: 'Late-todo shadow',
    tabId: DOC_PATH,
    tabType: 'note',
    filePath: DOC_PATH,
    hasFilePath: true,
    shareUrl: '/hop-studios/internal/late-todo-shadow',
    workspaceGroupId: 'hop',
  },
  folder: { type: 'folder', value: 'Documents', path: FOLDER_PATH },
  listRow: { type: 'document', value: 'Late-todo shadow.md', filePath: DOC_PATH },
} satisfies Record<string, GalleryMenuContext>

export const TAB_MENU: Record<string, GalleryMenuContext> = {
  inbox: MENU_CONTEXTS.inboxTab,
  documents: MENU_CONTEXTS.documentsTab,
  doc: MENU_CONTEXTS.docTab,
}

export const MODES: Array<{ id: PanelCollapseMode; label: string; icon: typeof Inbox }> = [
  { id: 'browse', label: 'Browse', icon: FolderTree },
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'flows', label: 'Flows', icon: Workflow },
  { id: 'chats', label: 'Chats', icon: Bot },
]

export type Layout = {
  frameWidth: number
  leftDocked: boolean
  leftPx: number
  agentDocked: boolean
  agentPx: number
  previewDocked: boolean
  previewPx: number
  autoHide: boolean
  pinned: boolean
}

export function columnWidth(docked: boolean, px: number): number {
  return docked ? px : RAIL
}

/**
 * Shrink docked columns down to their floors, then snap to the rail.
 * Auto-hide keeps the left list undocked unless it is pinned.
 */
export function previewWidth(docked: boolean, px: number): number {
  return docked ? px : 0
}

export function fitLayout(layout: Layout): Layout {
  let { leftDocked, leftPx, agentDocked, agentPx, previewDocked, previewPx, pinned } = layout
  if (layout.autoHide && !pinned) leftDocked = false
  leftPx = Math.max(LEFT_FLOOR, leftPx)
  agentPx = Math.max(AGENT_FLOOR, agentPx)
  previewPx = Math.max(PREVIEW_FLOOR, previewPx)
  const maxColumns = layout.frameWidth - CANVAS_MIN
  const width = () =>
    columnWidth(leftDocked, leftPx) +
    previewWidth(previewDocked, previewPx) +
    columnWidth(agentDocked, agentPx)

  if (agentDocked && width() > maxColumns) {
    agentPx = Math.max(AGENT_FLOOR, agentPx - (width() - maxColumns))
  }
  if (previewDocked && width() > maxColumns) {
    previewPx = Math.max(PREVIEW_FLOOR, previewPx - (width() - maxColumns))
  }
  if (leftDocked && width() > maxColumns) {
    leftPx = Math.max(LEFT_FLOOR, leftPx - (width() - maxColumns))
  }
  if (previewDocked && width() > maxColumns) previewDocked = false
  if (agentDocked && width() > maxColumns) agentDocked = false
  if (leftDocked && width() > maxColumns) {
    leftDocked = false
    pinned = false
  }
  return {
    ...layout,
    leftDocked,
    leftPx,
    agentDocked,
    agentPx,
    previewDocked,
    previewPx,
    pinned,
  }
}

export function canDock(frameWidth: number, otherPx: number, floor: number): boolean {
  return frameWidth - otherPx - CANVAS_MIN >= floor
}
